import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout';
import { 
  Recycle, 
  FileText, 
  Flame, 
  ClipboardCheck, 
  Package, 
  Factory,
  Cpu,
  Trash2,
  Truck,
  ArrowRight
} from 'lucide-react';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

const services = [
  {
    icon: Factory,
    title: 'Corporate Recyclable Management',
    description: 'We design and operate recyclable waste programs for corporate offices and campuses. Services include segregation systems, scheduled collection, recovery, and reporting.',
    href: '/services/corporate-recyclable-management',
    color: 'from-eco-green-500 to-eco-green-600',
  },
  {
    icon: Package,
    title: 'Factory Waste Management',
    description: 'We manage manufacturing waste streams from shop floor to authorized end-use. We focus on safety, compliance, and recovery.',
    href: '/services/factory-waste-management',
    color: 'from-slate-500 to-slate-600',
  },
  {
    icon: Recycle,
    title: 'Plastic Waste Management',
    description: 'We handle PET, HDPE, LDPE, PP, and multi-layered plastics. We provide collection, sorting, baling, and channelization to authorized recyclers.',
    href: '/services/plastic-waste-management',
    color: 'from-eco-green-500 to-eco-green-600',
  },
  {
    icon: FileText,
    title: 'Paper & Dry Recyclables',
    description: 'We recover OCC, mixed paper, cartons, and dry waste. We channelize to paper mills and authorized recyclers.',
    href: '/services/paper-dry-recyclables',
    color: 'from-eco-teal-500 to-eco-teal-600',
  },
  {
    icon: Flame,
    title: 'Low-Value Plastic & RDF',
    description: 'We process non-recyclable plastics into RDF for industrial co-processing. End-use includes cement kilns and industrial boilers.',
    href: '/services/low-value-plastic-rdf',
    color: 'from-orange-500 to-orange-600',
  },
  {
    icon: Cpu,
    title: 'E-Waste & Battery',
    description: 'We manage IT waste, electronics, lithium-ion and lead-acid batteries through authorized recyclers. We provide destruction and recycling certificates.',
    href: '/services/ewaste-battery',
    color: 'from-cyan-500 to-cyan-600',
  },
  {
    icon: ClipboardCheck,
    title: 'EPR Services',
    description: 'We support producers with collection setup, recycler onboarding, certificate management, and annual EPR reporting.',
    href: '/services/epr-services',
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: Trash2,
    title: 'Expired Product Disposal',
    description: 'We provide safe disposal of expired FMCG, pharmaceuticals, and industrial rejects with full documentation.',
    href: '/services/expired-product-disposal',
    color: 'from-red-500 to-red-600',
  },
  {
    icon: Truck,
    title: 'Scrap Trading',
    description: 'We trade MS scrap, aluminium, copper, brass, and stainless steel through authorized recycling channels.',
    href: '/services/scrap-trading',
    color: 'from-purple-500 to-purple-600',
  },
];

export function Services() {
  // SSR-safe animation props
  const heroAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const ctaAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Our Services | Eco Enviro - Waste Management & Recycling Solutions India</title>
        <meta name="title" content="Our Services | Eco Enviro - Waste Management & Recycling Solutions India" />
        <meta 
          name="description" 
          content="Comprehensive waste management services by Eco Enviro - plastic recycling, EPR services, e-waste management, factory waste, scrap trading, RDF production. Operating across 7 states in India." 
        />
        <meta name="keywords" content="waste management services India, plastic recycling services, EPR services India, e-waste management, factory waste management, scrap trading, RDF production, corporate waste management" />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/services`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services`} />
        <meta property="og:title" content="Our Services | Eco Enviro - Waste Management Solutions" />
        <meta property="og:description" content="Comprehensive waste management services - plastic recycling, EPR, e-waste, factory waste, scrap trading. Operating across 7 states in India." />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content="Our Services | Eco Enviro - Waste Management Solutions" />
        <meta name="twitter:description" content="Comprehensive waste management services - plastic recycling, EPR, e-waste, factory waste, scrap trading." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-medium rounded-full mb-6">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-eco-green-900 mb-6">
              Comprehensive Waste Solutions
            </h1>
            <p className="text-xl text-muted-foreground">
              From collection to compliance, we handle every aspect of responsible waste management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={heroAnim.initial}
                whileInView={heroAnim.animate}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={service.href}
                  className="group block h-full p-8 bg-white border border-eco-green-100 rounded-2xl hover:border-eco-green-200 hover:shadow-xl hover:shadow-eco-green-100/50 transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-eco-green-900 mb-3 group-hover:text-eco-green-700 transition-colors flex items-center gap-2">
                        {service.title}
                        <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-eco-green-800 to-eco-green-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={ctaAnim.initial}
            whileInView={ctaAnim.animate}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-eco-green-200 text-lg mb-8">
              Every business has unique waste management needs. Let's discuss how we can help you achieve your sustainability goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?quote=true"
                className="px-8 py-4 bg-white text-eco-green-800 font-semibold rounded-xl hover:bg-eco-green-50 transition-colors"
              >
                Get a Quote
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                Talk to Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
